import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { canViewPrices } from '../../utils/pricing'; // إخفاء عمود السعر عن الأدوار غير 1/3/7
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;

const FarrierProceduresList = () => {
  const { t, i18n } = useTranslation();
  const [procedures, setProcedures] = useState([]);
  const [search, setSearch] = useState('');
  const [showZeroOnly, setShowZeroOnly] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchProcedures = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${apiUrl}/farrier_procedures`);
      setProcedures(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('farrierProcedures.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProcedures();
  }, [fetchProcedures]);

  // تنبيه بيانات صفريّة: إجراء بيطار بلا سعر (احتاج طاقم العيادة تعبئته قبل الاستخدام).
  const isZeroRecord = useCallback((procedure) => Number(procedure?.price) <= 0, []);
  const zeroProceduresCount = useMemo(
    () => procedures.filter(isZeroRecord).length,
    [procedures, isZeroRecord]
  );

  const filteredProcedures = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = procedures;
    if (showZeroOnly) list = list.filter(isZeroRecord);
    if (!term) return list;
    return list.filter((procedure) => (
      String(procedure.procedure_name || '').toLowerCase().includes(term)
      || String(procedure.description || '').toLowerCase().includes(term)
      || String(procedure.price || '').toLowerCase().includes(term)
    ));
  }, [procedures, search, showZeroOnly, isZeroRecord]);

  const {
    page,
    rowsPerPage,
    paginatedItems,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredProcedures, [search]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`${apiUrl}/farrier_procedures/${deleteId}`);
      setDeleteId(null);
      setMessage(t('farrierProcedures.deleteSuccess'));
      await fetchProcedures();
    } catch (err) {
      setError(err?.response?.data?.error || t('farrierProcedures.deleteError'));
    } finally {
      setSaving(false);
    }
  }, [deleteId, fetchProcedures, t]);

  return (
    <Box
      className="smart-page-container"
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
    >
      <SettingsPageHeader
        title={t('farrierProcedures.title')}
        subtitle={t('farrierProcedures.pageHint')}
        count={procedures.length}
        actions={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/farrier-procedure-add')}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('farrierProcedures.addNew')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {zeroProceduresCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => setShowZeroOnly((value) => !value)}
          action={showZeroOnly ? (
            <Button size="small" onClick={(event) => { event.stopPropagation(); setShowZeroOnly(false); }} sx={{ fontWeight: 900 }}>
              {t('farrierProcedures.clearZeroFilter')}
            </Button>
          ) : undefined}
        >
          {t(showZeroOnly ? 'farrierProcedures.zeroFilterActive' : 'farrierProcedures.zeroAlert', { count: zeroProceduresCount })}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('farrierProcedures.search')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('procedure_name')}</TableCell>
                <TableCell>{t('description')}</TableCell>
                {canViewPrices() && <TableCell align="center">{t('price')}</TableCell>}
                <TableCell align="center" sx={{ width: 150 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((procedure, index) => (
                <TableRow key={procedure.procedure_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 190 }}>{procedure.procedure_name || '-'}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>{procedure.description || '-'}</TableCell>
                  {canViewPrices() && <TableCell align="center">{procedure.price ?? '-'}</TableCell>}
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit')}>
                        <IconButton
                          onClick={() => navigate(`/farrier-procedure-edit/${procedure.procedure_id}`)}
                          sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton onClick={() => setDeleteId(procedure.procedure_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredProcedures.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('farrierProcedures.noProcedures')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination
          count={filteredProcedures.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('farrierProcedures.confirmDelete')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarrierProceduresList;
